import { Items } from '@prisma/client';
import API_URL from '../apiconfig';
import {
  GiftCardsOverviewResult,
  GiftCardSettings,
  GiftCardSettingsPayload,
  SortableKeys,
  GiftCardsPage,
  GiftCardDetails,
} from '../types/gift-cards';
import axios from './api';
import client from '../utils/react-query/client';
import { SortValues } from '../types/ui';

export async function getGiftCardsOverview({
  businessId,
}: {
  businessId: string;
}) {
  const response = await axios.get(`${API_URL}/v1/gift-cards/overview`, {
    params: {
      businessId,
    },
  });

  return response.data.result as GiftCardsOverviewResult;
}

export async function getGiftCards({
  businessId,
  sort,
  cursor,
}: {
  businessId: string;
  sort?: SortValues<SortableKeys>;
  cursor?: string;
}) {
  const response = await axios.get(`${API_URL}/v1/gift-cards`, {
    params: {
      businessId,
      sortOrder: sort?.order,
      sortOrderBy: sort?.orderBy,
      cursor,
      limit: 20,
    },
  });

  return response.data as GiftCardsPage;
}

export async function getGiftCardItem({ businessId }: { businessId: string }) {
  const response = await axios.get(`${API_URL}/v1/gift-cards/item`, {
    params: {
      businessId,
    },
  });

  return response.data.result as Items;
}

export async function updateGiftCardSettings(
  businessId: string,
  payload: GiftCardSettingsPayload,
) {
  const response = await axios.patch(`${API_URL}/v1/gift-cards/settings`, {
    businessId,
    enabled: payload.enabled,
    defaultGiftCardBalance: payload.defaultGiftCardBalance,
    expirationDays: payload.expirationDays || null,
  });

  return response.data.result as GiftCardSettings;
}

export async function invalidateGiftCardItem() {
  await client.invalidateQueries({
    queryKey: ['gift-card-item'],
    exact: false,
  });
}

export async function resetGiftCards() {
  await client.resetQueries({
    queryKey: ['gift-cards'],
    exact: false,
  });
}

export async function invalidateGiftCards() {
  await client.invalidateQueries({
    queryKey: ['gift-cards'],
    exact: false,
  });
}

export async function invalidateGiftCard(giftCardId: string) {
  await client.invalidateQueries({ queryKey: ['gift-card', giftCardId] });
}

export async function invalidateGiftCardsOverview() {
  await client.invalidateQueries({
    queryKey: ['gift-cards-overview'],
    exact: false,
  });
}

export async function invalidateGiftCardsPage(
  invalidateType: 'reset' | 'invalidate' = 'reset',
) {
  await invalidateGiftCardsOverview();
  if (invalidateType === 'reset') {
    await resetGiftCards();
  } else {
    await invalidateGiftCards();
  }
}

export async function resendGiftCardEmail({
  giftCardId,
  email,
}: {
  giftCardId: string;
  email?: string;
}): Promise<{ message: string }> {
  const response = await axios.post(
    `${API_URL}/v1/gift-cards/${giftCardId}/resend`,
    {
      email,
    },
  );

  return response.data.result;
}

export async function updateGiftCardBalance(
  giftCardId: string,
  balance: number,
): Promise<{ id: string; currentBalance: number }> {
  const response = await axios.patch(
    `${API_URL}/v1/gift-cards/${giftCardId}/balance`,
    {
      balance,
    },
  );

  return response.data.result;
}

export async function getCustomerGiftCardBalance({
  businessId,
  customerId,
  phoneNumber,
}: {
  businessId: string;
  customerId?: string;
  phoneNumber?: string;
}): Promise<{ availableBalance: number }> {
  const response = await axios.get(
    `${API_URL}/v1/gift-cards/customer/balance`,
    {
      params: {
        businessId,
        customerId: customerId || undefined,
        phoneNumber: phoneNumber || undefined,
      },
    },
  );

  return response.data.result;
}

export async function applyGiftCardCode({
  businessId,
  code,
  customerId,
}: {
  businessId: string;
  code: string;
  customerId: string;
}): Promise<{
  message: string;
  giftCardId: string;
  currentBalance: number;
}> {
  const response = await axios.post(`${API_URL}/v1/gift-cards/customer/apply`, {
    businessId,
    code,
    customerId,
  });

  return response.data.result;
}

export async function getGiftCardDetails(
  giftCardId: string,
): Promise<GiftCardDetails> {
  const response = await axios.get(`${API_URL}/v1/gift-cards/${giftCardId}`);
  return response.data.result;
}

export async function invalidateCustomerGiftCardBalances() {
  await client.invalidateQueries({
    queryKey: ['customer-gift-card-balance'],
    exact: false,
  });
}
